/**
 * ErrorTable — the row-level validation errors of a rejected Path A run
 * (sheet 12 callouts 5, 6 and 7; mission 6 WP07 T042; FR-010; U.1, U.2, D.2;
 * research §1.4; post-plan S12; contracts/ui-api.md "Rejected (sheet 12)").
 *
 * - Reads `['upload', id]` (staleTime 0). The inline window the API serves is
 *   the only list held: filtered in memory by the selected column, rendered in
 *   the API's order (row, then header order within a row), never re-sorted.
 * - Row, Column, Expected, Got and Hint, the columns sheet 12 draws. Row
 *   numbers and column names are mono.
 * - `got` is the customer's verbatim value: a plain text node, never a
 *   `title`, a tooltip or an `aria-label` (S12).
 * - At most LIST_CAP (200) rows render at a time (NFR-005; mission 6 review
 *   follow-up 16, A-3). Whenever fewer rows render than exist (the API cut
 *   the window, the cap cut it, or both), the caption states the shown and
 *   total counts and points at the report (register 6). Under a column filter
 *   both counts are the column's.
 * - An omitted `errors` renders register 35's `rowErrorsUnavailable`; the
 *   counts above stay, because they are Class 2 (D.2). Mission 8 WP10 (T054;
 *   FR-020 A-L5): it is never the retention sentence, which may be untrue. The
 *   upload read carries no retention signal. The backend omits `errors` in
 *   three cases: the report object is gone (the retention sweep; report_url is
 *   kept and answers 410), the upload has no report key (report_url absent
 *   too), or the rebuilt window is empty. A store failure is not one of them:
 *   the whole read answers 503 and renders the read error above. The 410 comes
 *   only from GET /v1/uploads/{id}/report, a download with its audit event, so
 *   it is not probed here. The retention sentence renders where the API says
 *   so: a 410 `D.2` problem (ProblemBand).
 */
import { useQuery } from '@tanstack/react-query'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { ApiError } from '@/lib/api'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { LIST_CAP, uploadQuery } from '@/lib/queries'

/** The column headers sheet 12 draws (callouts 2 and 6). */
const HEADS = ['Row', 'Column', 'Expected', 'Got', 'Hint'] as const

export interface ErrorTableProps {
  uploadId: string
  /** The selected column group, or null for every column. Component state, never a query key. */
  column: string | null
}

export function ErrorTable({ uploadId, column }: ErrorTableProps) {
  const upload = useQuery(uploadQuery(uploadId))

  if (upload.isError) {
    return upload.error instanceof ApiError ? (
      <ProblemBand problem={upload.error.problem} />
    ) : (
      <Band kind="blocked">
        <p>{copyText('genericError')}</p>
      </Band>
    )
  }

  const data = upload.data
  if (data && data.errors === undefined) {
    return <p className="text-body text-navy-700">{copyText('rowErrorsUnavailable')}</p>
  }
  const errors = data?.errors ?? []
  const matching = column === null ? errors : errors.filter((e) => e.column === column)
  const rows = matching.slice(0, LIST_CAP)
  const total =
    column === null
      ? Math.max(data?.error_count ?? 0, errors.length)
      : Math.max(data?.errors_by_column?.[column] ?? 0, matching.length)

  return (
    <Table>
      {data !== undefined && rows.length < total ? (
        <TableCaption>
          {PROVISIONAL_COPY.truncationFooterPathA.render({ shown: count(rows.length), total: count(total) })}
        </TableCaption>
      ) : null}
      <TableHeader>
        <TableRow>
          {HEADS.map((h) => (
            <TableHead key={h}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data === undefined ? (
          <TableSkeletonRows columns={HEADS.length} />
        ) : (
          rows.map((e, i) => (
            <TableRow key={`${e.row}:${e.column}:${i}`} dense>
              <TableCell>
                <Mono>{count(e.row)}</Mono>
              </TableCell>
              <TableCell>
                <Mono>{e.column}</Mono>
              </TableCell>
              <TableCell>{e.expected}</TableCell>
              <TableCell>{e.got}</TableCell>
              <TableCell>{e.hint}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}
